import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import ExportButtons from '../components/ExportButtons';

const UNITS = ['Nos', 'Mtr', 'Box', 'Roll'];
const ALL_CAMPUS = u => ['admin', 'it_head', 'management'].includes(u.role);

export default function Consumables({ embedded }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('stock');
  const [campuses, setCampuses] = useState([]);
  const [items, setItems] = useState([]);
  const [moves, setMoves] = useState([]);

  const loadItems = useCallback(() => { api.get('/consumables').then(r => setItems(r.data)).catch(()=>{}); }, []);
  const loadMoves = useCallback(() => { api.get('/consumables/movements').then(r => setMoves(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    loadItems(); loadMoves();
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c => c.active && (ALL_CAMPUS(user) || (user.campus_ids||[]).includes(c.id))))).catch(()=>{});
  }, [loadItems, loadMoves, user]);

  const refresh = () => { loadItems(); loadMoves(); };
  const tabs = [['stock','Stock'],['in','Stock In'],['out','Stock Out'],['log','Consumption Log'],['reports','Reports']];

  return (
    <div>
      {!embedded && (
        <div style={{ marginBottom:16 }}>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Consumables</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Quantity-based IT inventory — cables, connectors, batteries and accessories.</p>
        </div>
      )}
      <div style={{ display:'flex', gap:0, marginBottom:18, borderBottom:'1px solid var(--border)', flexWrap:'wrap' }}>
        {tabs.map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'9px 16px', fontSize:13, cursor:'pointer', background:'transparent', border:'none', whiteSpace:'nowrap',
              borderBottom: tab===id?'2px solid var(--accent)':'2px solid transparent',
              color: tab===id?'var(--accent)':'var(--text2)', fontWeight: tab===id?600:400, marginBottom:-1 }}>{label}</button>
        ))}
      </div>

      {tab==='stock'   && <StockTab items={items} campuses={campuses} onChange={refresh} />}
      {tab==='in'      && <MoveForm kind="in"  items={items} onDone={refresh} />}
      {tab==='out'     && <MoveForm kind="out" items={items} onDone={refresh} />}
      {tab==='log'     && <LogTab moves={moves} />}
      {tab==='reports' && <ReportsTab items={items} moves={moves} />}
    </div>
  );
}

function StockTab({ items, campuses, onChange }) {
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const blank = { name:'', category:'', campus_id: campuses[0]?String(campuses[0].id):'', unit_type:'Nos', min_stock:0, remarks:'' };
  const save = async () => {
    setBusy(true);
    try {
      if (modal.id) await api.put(`/consumables/${modal.id}`, modal);
      else await api.post('/consumables', { ...modal, campus_id: parseInt(modal.campus_id) });
      setModal(null); onChange();
    } catch(e) { alert(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:12 }}>
        <ExportButtons filename="current-stock" title="Current Stock" rows={items}
          columns={[{key:'name',label:'Item'},{key:'category',label:'Category'},{key:'campus_name',label:'Campus'},{key:'unit_type',label:'Unit'},{key:'current_stock',label:'Current'},{key:'min_stock',label:'Minimum'}]} />
        <button className="btn btn-primary btn-sm" onClick={() => setModal({ ...blank })}>+ Add Item</button>
      </div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!items.length ? <div className="empty-state"><p>No consumables yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Item</th><th>Category</th><th>Campus</th><th>Unit</th><th>Current</th><th>Min</th><th>Status</th><th></th></tr></thead>
            <tbody>{items.map(i => (
              <tr key={i.id}>
                <td style={{ fontSize:13, fontWeight:500 }}>{i.name}</td>
                <td style={{ fontSize:12 }}>{i.category||'—'}</td>
                <td style={{ fontSize:12 }}>{i.campus_name}</td>
                <td style={{ fontSize:12 }}>{i.unit_type}</td>
                <td style={{ fontSize:13, fontWeight:600 }}>{(+i.current_stock).toFixed(2).replace(/\.00$/,'')}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{(+i.min_stock).toFixed(2).replace(/\.00$/,'')}</td>
                <td>{i.low ? <span className="badge badge-faulty">Low</span> : <span className="badge badge-completed">OK</span>}</td>
                <td><button className="btn btn-secondary btn-sm" onClick={() => setModal({ id:i.id, name:i.name, category:i.category||'', unit_type:i.unit_type, min_stock:i.min_stock, remarks:i.remarks||'', campus_id:String(i.campus_id) })}>Edit</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:460 }}>
            <div className="modal-header"><span className="modal-title">{modal.id?'Edit':'Add'} Consumable</span><button className="modal-close" onClick={() => setModal(null)}>×</button></div>
            <div className="form-group"><label>Item Name *</label><input value={modal.name} onChange={e => setModal(m=>({...m,name:e.target.value}))} placeholder="e.g. LAN Cable" /></div>
            <div className="form-row">
              <div className="form-group"><label>Category</label><input value={modal.category} onChange={e => setModal(m=>({...m,category:e.target.value}))} placeholder="e.g. Cable" /></div>
              <div className="form-group"><label>Unit Type</label><select value={modal.unit_type} onChange={e => setModal(m=>({...m,unit_type:e.target.value}))}>{UNITS.map(u=><option key={u} value={u}>{u}</option>)}</select></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Campus *</label>
                <select value={modal.campus_id} disabled={!!modal.id} onChange={e => setModal(m=>({...m,campus_id:e.target.value}))}>
                  <option value="">— Select —</option>{campuses.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div className="form-group"><label>Minimum Stock</label><input type="number" value={modal.min_stock} onChange={e => setModal(m=>({...m,min_stock:e.target.value}))} /></div>
            </div>
            <div className="form-group"><label>Remarks</label><input value={modal.remarks} onChange={e => setModal(m=>({...m,remarks:e.target.value}))} /></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy || !modal.name.trim() || !modal.campus_id}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MoveForm({ kind, items, onDone }) {
  const [f, setF] = useState({ consumable_id:'', quantity:'', vendor:'', invoice_no:'', purpose:'', room_id:'', remarks:'' });
  const [rooms, setRooms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const item = items.find(i => String(i.id) === String(f.consumable_id));
  useEffect(() => {
    if (kind==='out' && item) api.get(`/master/rooms/${item.campus_id}`).then(r => setRooms(r.data.filter(x=>x.active))).catch(()=>setRooms([]));
  }, [kind, item]);

  const submit = async () => {
    if (!f.consumable_id) { setMsg('Select an item'); return; }
    if (!(parseFloat(f.quantity) > 0)) { setMsg('Enter a quantity'); return; }
    if (kind==='out' && !f.purpose.trim()) { setMsg('Purpose is required'); return; }
    setBusy(true); setMsg('');
    try {
      const url = kind==='in' ? `/consumables/${f.consumable_id}/stock-in` : `/consumables/${f.consumable_id}/stock-out`;
      const r = await api.post(url, kind==='in'
        ? { quantity:f.quantity, vendor:f.vendor, invoice_no:f.invoice_no, remarks:f.remarks }
        : { quantity:f.quantity, purpose:f.purpose, room_id:f.room_id||undefined, remarks:f.remarks });
      setMsg(`✓ Done — new balance ${r.data.balance} ${item?.unit_type||''}`);
      setF({ consumable_id:'', quantity:'', vendor:'', invoice_no:'', purpose:'', room_id:'', remarks:'' });
      onDone();
    } catch(e) { setMsg(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ maxWidth:560 }}>
      <div style={{ fontSize:14, fontWeight:600, marginBottom:12 }}>{kind==='in'?'Stock In — add purchased inventory':'Stock Out — record consumption'}</div>
      <div className="form-group"><label>Item *</label>
        <select value={f.consumable_id} onChange={e => setF(p=>({...p,consumable_id:e.target.value}))}>
          <option value="">— Select item —</option>
          {items.map(i => <option key={i.id} value={i.id}>{i.name} ({i.campus_name}) — {(+i.current_stock).toFixed(2).replace(/\.00$/,'')} {i.unit_type}</option>)}
        </select></div>
      <div className="form-group" style={{ maxWidth:200 }}><label>Quantity * {item?`(${item.unit_type})`:''}</label>
        <input type="number" value={f.quantity} onChange={e => setF(p=>({...p,quantity:e.target.value}))} /></div>
      {kind==='in' ? (
        <div className="form-row">
          <div className="form-group"><label>Vendor</label><input value={f.vendor} onChange={e => setF(p=>({...p,vendor:e.target.value}))} /></div>
          <div className="form-group"><label>Invoice No.</label><input value={f.invoice_no} onChange={e => setF(p=>({...p,invoice_no:e.target.value}))} /></div>
        </div>
      ) : (
        <>
          <div className="form-group"><label>Purpose *</label><input value={f.purpose} onChange={e => setF(p=>({...p,purpose:e.target.value}))} placeholder="e.g. Network Extension" /></div>
          <div className="form-group"><label>Room / Location</label>
            <select value={f.room_id} onChange={e => setF(p=>({...p,room_id:e.target.value}))} disabled={!item}>
              <option value="">— Select room —</option>{rooms.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
            </select></div>
        </>
      )}
      <div className="form-group"><label>Remarks</label><input value={f.remarks} onChange={e => setF(p=>({...p,remarks:e.target.value}))} /></div>
      {msg && <div style={{ fontSize:13, color: msg.startsWith('✓')?'var(--green)':'var(--red)', marginBottom:8 }}>{msg}</div>}
      <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy?'Saving…':(kind==='in'?'Add Stock':'Record Consumption')}</button>
    </div>
  );
}

const moveRow = m => ({ ...m,
  when: new Date(m.created_at).toLocaleString(),
  qty: `${m.type==='out'?'-':'+'}${(+m.quantity).toFixed(2).replace(/\.00$/,'')} ${m.unit_type}`,
});

function LogTab({ moves }) {
  const [type, setType] = useState('');
  const rows = moves.filter(m => !type || m.type===type).map(moveRow);
  const cols = [{key:'when',label:'Date'},{key:'item_name',label:'Item'},{key:'qty',label:'Qty'},{key:'type',label:'Type'},
    {key:'purpose',label:'Purpose'},{key:'campus_name',label:'Campus'},{key:'room_name',label:'Room'},{key:'by_name',label:'User'},
    {key:'balance_after',label:'Remaining'},{key:'task_ref',label:'Ticket/Task'}];
  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:12 }}>
        <select value={type} onChange={e => setType(e.target.value)} style={{ padding:'6px 8px' }}>
          <option value="">All movements</option><option value="in">Stock In</option><option value="out">Stock Out</option>
        </select>
        <ExportButtons filename="consumption-log" title="Consumption Log" rows={rows} columns={cols} />
      </div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No movements yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Item</th><th>Qty</th><th>Type</th><th>Purpose</th><th>Campus</th><th>Room</th><th>User</th><th>Remaining</th><th>Ref</th></tr></thead>
            <tbody>{rows.map(m => (
              <tr key={m.id}>
                <td style={{ fontSize:12, color:'var(--text2)', whiteSpace:'nowrap' }}>{m.when}</td>
                <td style={{ fontSize:13 }}>{m.item_name}</td>
                <td style={{ fontSize:13, color: m.type==='out'?'var(--red)':'var(--green)' }}>{m.qty}</td>
                <td><span className={`badge ${m.type==='out'?'badge-pending':'badge-completed'}`}>{m.type}</span></td>
                <td style={{ fontSize:12 }}>{m.purpose||(m.type==='in'?('In'+(m.invoice_no?' · '+m.invoice_no:'')):'—')}</td>
                <td style={{ fontSize:12 }}>{m.campus_name}</td>
                <td style={{ fontSize:12 }}>{m.room_name||'—'}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{m.by_name||'—'}</td>
                <td style={{ fontSize:12 }}>{(+m.balance_after).toFixed(2).replace(/\.00$/,'')} {m.unit_type}</td>
                <td><span className="mono" style={{ fontSize:11, color:'var(--accent)' }}>{m.task_ref||'—'}</span></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function ReportsTab({ items, moves }) {
  const stockCols = [{key:'name',label:'Item'},{key:'category',label:'Category'},{key:'campus_name',label:'Campus'},{key:'unit_type',label:'Unit'},{key:'current_stock',label:'Current'},{key:'min_stock',label:'Minimum'}];
  const movRows = moves.map(moveRow);
  const movCols = [{key:'when',label:'Date'},{key:'item_name',label:'Item'},{key:'qty',label:'Qty'},{key:'type',label:'Type'},{key:'vendor',label:'Vendor'},{key:'invoice_no',label:'Invoice'},{key:'purpose',label:'Purpose'},{key:'campus_name',label:'Campus'},{key:'room_name',label:'Room'},{key:'by_name',label:'User'},{key:'balance_after',label:'Remaining'}];
  const Row = ({ label, rows, columns, file }) => (
    <div className="card" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
      <div style={{ fontSize:13 }}>{label} <span style={{ color:'var(--text2)' }}>· {rows.length} rows</span></div>
      <ExportButtons filename={file} title={label} rows={rows} columns={columns} />
    </div>
  );
  return (
    <div style={{ maxWidth:720 }}>
      <Row label="Current Stock Report" rows={items} columns={stockCols} file="current-stock" />
      <Row label="Low Stock Report" rows={items.filter(i=>i.low)} columns={stockCols} file="low-stock" />
      <Row label="Stock In Report" rows={movRows.filter(m=>m.type==='in')} columns={movCols} file="stock-in" />
      <Row label="Stock Out Report" rows={movRows.filter(m=>m.type==='out')} columns={movCols} file="stock-out" />
      <Row label="Consumption Report (all movements)" rows={movRows} columns={movCols} file="consumption" />
    </div>
  );
}
